/**
 * Data of the public landing: the live execution-quality aggregate and the public ticker, and
 * the small formatting helpers the sections share. Every figure on the page comes from here — nothing is invented.
 */
import { type Lang, numberLocaleFor } from "@prop/i18n";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { get } from "../../lib/api";
import { useHydrated } from "../../lib/hydration";
import type { TapeItem } from "./LiveBits";

export type Market = "crypto" | "fx" | "futures";
type Ticker = { prices: Array<{ instrumentId: string; price: string; minute: string }> };
export type Quality = {
  window: string;
  generatedAt: string;
  fills: number;
  accounts: number;
  latencyMs: { p50: number; p99: number } | null;
  quoteAgeMs: { p50: number; p99: number } | null;
  walkedPct: number | null;
  policyVersion: string | null;
  lastFillAt: string | null;
};

export const MARKETS: Market[] = ["crypto", "fx", "futures"];
/**
 * Real screenshots of the platform, one set per UI language (apps/web/scripts/shoot-landing.mjs). Spanish has no set of
 * its own yet and shows the English one (ADR-0065) until the script is re-run for es.
 */
const SHOT_LANG: Record<string, string> = { es: "en" };
const shotDir = (lang: string) => SHOT_LANG[lang] ?? lang;
export const shot = (lang: string, file: string) => `/landing/${shotDir(lang)}/${file}.jpg`;
/** Generated pictures: renders of objects and editorial photographs; none shows a customer, a result or a review. */
export const art = (file: string) => `/landing/art/${file}.jpg`;
/** AVIF / WebP variants of the same pictures (`<base>-<width>.<ext>`, ADR-0048). */
export const artBase = (file: string) => `/landing/art/${file}`;
export const shotBase = (lang: string, file: string) => `/landing/${shotDir(lang)}/${file}`;
export const SHOT_WIDTHS = [800, 1600, 2880] as const;
/** The hero photograph's sizes — the same string is in index.html's preload (keep them equal). */
export const HERO_SIZES = "(max-width: 760px) 100vw, 64vw";

export const num = (v: string | number, lang: Lang, digits = 0) => Number(v).toLocaleString(numberLocaleFor(lang), { maximumFractionDigits: digits });
export const useQuality = () => useQuery({ queryKey: ["public-quality"], queryFn: () => get<Quality>("/public/execution-quality"), refetchInterval: 15_000 });
const useTicker = () => useQuery({ queryKey: ["public-ticker"], queryFn: () => get<Ticker>("/public/ticker"), refetchInterval: 5000 });

/** Live BTC last trade with a short up/down flash on change. */
export function useBtc() {
  const ticker = useTicker();
  const btc = ticker.data?.prices.find((p) => p.instrumentId.startsWith("BTC"));
  const prev = useRef<number | null>(null);
  const [dir, setDir] = useState<"up" | "down" | "">("");
  useEffect(() => {
    if (!btc) return;
    const v = Number(btc.price);
    if (prev.current !== null && v !== prev.current) {
      setDir(v > prev.current ? "up" : "down");
      const h = setTimeout(() => setDir(""), 900);
      prev.current = v;
      return () => clearTimeout(h);
    }
    prev.current = v;
    return undefined;
  }, [btc]);
  return { price: btc?.price, dir };
}

/** The tape: every instrument of the public ticker with its move since the previous poll. */
export function useTape(lang: Lang): TapeItem[] {
  const ticker = useTicker();
  const prev = useRef(new Map<string, { price: number; dir: TapeItem["dir"] }>());
  return useMemo(() => {
    const out: TapeItem[] = [];
    for (const p of ticker.data?.prices ?? []) {
      const v = Number(p.price);
      const before = prev.current.get(p.instrumentId);
      const dir = !before ? "" : v > before.price ? "up" : v < before.price ? "down" : before.dir;
      prev.current.set(p.instrumentId, { price: v, dir });
      out.push({ id: p.instrumentId, label: p.instrumentId.split(".")[0] ?? p.instrumentId, price: num(v, lang, v < 10 ? 5 : 2), dir });
    }
    return out;
  }, [ticker.data, lang]);
}

/** A clock that ticks once a minute: the hero's dateline. Null in the prerendered HTML and while hydrating (ADR-0061). */
export function useNow(): Date | null {
  const hydrated = useHydrated();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  return hydrated ? now : null;
}
